import React, { useState, useEffect } from 'react';
import {
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Chip
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Receipt as ReceiptIcon,
  People as PeopleIcon,
  Settings as SettingsIcon,
  ArrowForward as ArrowForwardIcon,
  CheckCircleOutline as CheckIcon
} from '@mui/icons-material';
import { supabase } from '../supabaseClient';
import { format } from 'date-fns';

const Dashboard = ({ onNavigate }) => {
  const [stats, setStats] = useState({
    totalBilled: 0,
    cgstSgst: 0,
    igst: 0,
    billCount: 0
  });
  const [recentBills, setRecentBills] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      // 1. Fetch all active bills
      const { data: bills, error: billsErr } = await supabase
        .from('bills')
        .select('*')
        .is('deleted_at', null)
        .order('created_at', { ascending: false });

      if (billsErr) throw billsErr;

      const now = new Date();
      const currentMonth = now.getMonth();
      const currentYear = now.getFullYear();

      let total = 0;
      let cgstSgst = 0;
      let igst = 0;
      let currentMonthBillsCount = 0;

      // Filter bills for the current month
      const currentMonthBills = (bills || []).filter(bill => {
        const date = new Date(bill.bill_date || bill.created_at);
        return date.getFullYear() === currentYear && date.getMonth() === currentMonth;
      });

      currentMonthBills.forEach(bill => {
        currentMonthBillsCount++;
        const grandTotal = parseFloat(bill.total_amount || 0);
        total += grandTotal;

        if (bill.gst_type === 'instate') {
          const taxable = grandTotal / 1.18;
          const tax = taxable * 0.09;
          cgstSgst += (tax * 2); // SGST + CGST
        } else if (bill.gst_type === 'outofstate') {
          const taxable = grandTotal / 1.18;
          igst += (taxable * 0.18);
        }
      });

      setStats({
        totalBilled: total,
        cgstSgst: cgstSgst,
        igst: igst,
        billCount: currentMonthBillsCount
      });

      // Recent 5 bills of this month
      setRecentBills(currentMonthBills.slice(0, 5));
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (val) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(val);
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="700" color="text.primary" gutterBottom>
          Dashboard Overview
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Real-time billing analytics for the <strong>current month</strong>, recent logs, and quick actions.
        </Typography>
      </Box>

      {/* Analytics Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ 
            height: '100%', 
            borderRadius: 3, 
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)', 
            border: '1px solid #e2e8f0',
            transition: 'transform 0.2s, box-shadow 0.2s',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 16px rgba(15, 23, 42, 0.08)',
              borderColor: '#6366f1'
            }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle2" fontWeight="600" color="text.secondary">
                  Total Billed Revenue (This Month)
                </Typography>
                <Avatar sx={{ bgcolor: 'rgba(99, 102, 241, 0.1)', color: '#6366f1' }}>
                  <TrendingUpIcon />
                </Avatar>
              </Box>
              <Typography variant="h4" fontWeight="700" color="#0f172a" sx={{ mb: 1 }}>
                {formatCurrency(stats.totalBilled)}
              </Typography>
              <Chip 
                label={`${stats.billCount} Invoices this month`} 
                size="small" 
                color="success" 
                variant="soft" 
                sx={{ fontWeight: '600', bgcolor: 'rgba(22, 163, 74, 0.1)', color: '#16a34a' }}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ 
            height: '100%', 
            borderRadius: 3, 
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)', 
            border: '1px solid #e2e8f0',
            transition: 'transform 0.2s, box-shadow 0.2s',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 16px rgba(15, 23, 42, 0.08)',
              borderColor: '#6366f1'
            }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle2" fontWeight="600" color="text.secondary">
                  CGST & SGST Collected
                </Typography>
                <Avatar sx={{ bgcolor: 'rgba(99, 102, 241, 0.1)', color: '#6366f1' }}>
                  <CheckIcon />
                </Avatar>
              </Box>
              <Typography variant="h4" fontWeight="700" color="#0f172a" sx={{ mb: 1 }}>
                {formatCurrency(stats.cgstSgst)}
              </Typography>
              <Typography variant="caption" color="text.secondary" fontWeight="500">
                Intra-state collections this month
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ 
            height: '100%', 
            borderRadius: 3, 
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)', 
            border: '1px solid #e2e8f0',
            transition: 'transform 0.2s, box-shadow 0.2s',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 16px rgba(15, 23, 42, 0.08)',
              borderColor: '#6366f1'
            }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle2" fontWeight="600" color="text.secondary">
                  IGST Collected
                </Typography>
                <Avatar sx={{ bgcolor: 'rgba(99, 102, 241, 0.1)', color: '#6366f1' }}>
                  <ReceiptIcon />
                </Avatar>
              </Box>
              <Typography variant="h4" fontWeight="700" color="#0f172a" sx={{ mb: 1 }}>
                {formatCurrency(stats.igst)}
              </Typography>
              <Typography variant="caption" color="text.secondary" fontWeight="500">
                Inter-state collections this month
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Main content split */}
      <Grid container spacing={3}>
        {/* Left - Recent Invoices */}
        <Grid item xs={12} lg={8}>
          <Paper sx={{ 
            p: 3, 
            borderRadius: 3, 
            border: '1px solid #e2e8f0', 
            boxShadow: '0 2px 8px rgba(0,0,0,0.02)'
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" fontWeight="700" color="#0f172a">
                Recent Invoices Ledger
              </Typography>
              <Button 
                endIcon={<ArrowForwardIcon />} 
                onClick={() => onNavigate(2)} // Navigate to ledger database
                sx={{ fontWeight: '600', color: '#6366f1' }}
              >
                View Ledger
              </Button>
            </Box>

            <TableContainer>
              <Table size="medium">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8fafc' }}>
                    <TableCell sx={{ fontWeight: '600', color: '#475569' }}>Bill Number</TableCell>
                    <TableCell sx={{ fontWeight: '600', color: '#475569' }}>Customer Name</TableCell>
                    <TableCell sx={{ fontWeight: '600', color: '#475569' }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: '600', color: '#475569' }}>Amount</TableCell>
                    <TableCell sx={{ fontWeight: '600', color: '#475569' }}>GST Type</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        Loading invoices...
                      </TableCell>
                    </TableRow>
                  ) : recentBills.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        No invoices generated yet.
                      </TableCell>
                    </TableRow>
                  ) : (
                    recentBills.map((bill) => (
                      <TableRow key={bill.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                        <TableCell sx={{ fontWeight: '600', color: '#6366f1' }}>
                          #{bill.bill_number}
                        </TableCell>
                        <TableCell sx={{ fontWeight: '500' }}>
                          {bill.customer_name}
                        </TableCell>
                        <TableCell sx={{ color: 'text.secondary' }}>
                          {bill.bill_date ? format(new Date(bill.bill_date), 'dd/MM/yyyy') : format(new Date(bill.created_at), 'dd/MM/yyyy')}
                        </TableCell>
                        <TableCell sx={{ fontWeight: '700', color: '#0f172a' }}>
                          {formatCurrency(bill.total_amount)}
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={
                              bill.gst_type === 'instate' ? 'In State' : 
                              bill.gst_type === 'outofstate' ? 'Out of State' : 'No GST'
                            } 
                            size="small"
                            sx={{
                              fontWeight: '600',
                              fontSize: '0.75rem',
                              bgcolor: 
                                bill.gst_type === 'instate' ? 'rgba(34, 197, 94, 0.1)' : 
                                bill.gst_type === 'outofstate' ? 'rgba(139, 92, 246, 0.1)' : 'rgba(100, 116, 139, 0.1)',
                              color: 
                                bill.gst_type === 'instate' ? '#15803d' : 
                                bill.gst_type === 'outofstate' ? '#6d28d9' : '#475569'
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Right - Quick Action Panel */}
        <Grid item xs={12} lg={4}>
          <Paper sx={{ 
            p: 3, 
            borderRadius: 3, 
            border: '1px solid #e2e8f0', 
            boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
            height: '100%',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <Typography variant="h6" fontWeight="700" color="#0f172a" sx={{ mb: 3 }}>
              Quick Actions
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, flexGrow: 1 }}>
              <Button
                variant="contained"
                size="large"
                startIcon={<ReceiptIcon />}
                onClick={() => onNavigate(0)} // Navigate to Generate Bill
                sx={{ 
                  py: 2, 
                  borderRadius: 2, 
                  fontWeight: '600', 
                  bgcolor: '#6366f1',
                  '&:hover': { bgcolor: '#4f46e5' }
                }}
              >
                Create New Invoice
              </Button>

              <Button
                variant="outlined"
                size="large"
                startIcon={<PeopleIcon />}
                onClick={() => onNavigate(1)} // Navigate to Customers
                sx={{ 
                  py: 2, 
                  borderRadius: 2, 
                  fontWeight: '600', 
                  borderColor: '#cbd5e1',
                  color: '#475569',
                  '&:hover': { borderColor: '#94a3b8', bgcolor: '#f8fafc' }
                }}
              >
                Manage Customers
              </Button>

              <Button
                variant="outlined"
                size="large"
                startIcon={<SettingsIcon />}
                onClick={() => onNavigate(3)} // Navigate to Settings
                sx={{ 
                  py: 2, 
                  borderRadius: 2, 
                  fontWeight: '600', 
                  borderColor: '#cbd5e1',
                  color: '#475569',
                  '&:hover': { borderColor: '#94a3b8', bgcolor: '#f8fafc' }
                }}
              >
                Company Configuration
              </Button>
            </Box>

            <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid #e2e8f0', textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary" fontWeight="500">
                Logged in as Administrator
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
